import React, { useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Bar } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";

import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Typography from "@material-ui/core/Typography";
import { useTheme } from "@material-ui/core/styles";
import VisibilityIcon from "@material-ui/icons/Visibility";

import { i18n } from "../../translate/i18n";
import NoInteractionTicketsModal from "../../components/Dashboard/NoInteractionTicketsModal";
import { getChartTextColor, getChartGridColor } from "./chartTheme";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const BAR_COLOR = "#CC0036";

const buildOptions = (textColor, gridColor) => ({
  responsive: true,
  scales: {
    x: {
      ticks: { color: textColor },
      grid: { color: gridColor }
    },
    y: {
      beginAtZero: true,
      ticks: { color: textColor, precision: 0 },
      grid: { color: gridColor }
    }
  },
  plugins: {
    legend: {
      position: "top",
      display: true,
      labels: { color: textColor }
    },
    title: {
      display: true,
      text: i18n.t("dashboard.noInteraction.chartTitle"),
      color: textColor
    },
    // Valor dentro da barra (mesmo padrão do gráfico "Total de Conversas por Usuário"):
    // acima da barra ele fica cortado quando o valor bate no topo da escala.
    datalabels: {
      display: true,
      anchor: "start",
      offset: -30,
      align: "start",
      color: "#fff",
      textStrokeColor: "#000",
      textStrokeWidth: 2,
      font: { size: 20, weight: "bold" },
      formatter: value => {
        const total = Math.trunc(Number(value) || 0);
        return total > 0 ? total : "";
      }
    }
  }
});

// Chamados finalizados sem interação com o contato, por atendente.
// Os dados vêm do Dashboard (que já busca o resumo junto com os contadores);
// clicar em uma linha abre a lista detalhada dos tickets daquele atendente.
export const ChartsClosedWithoutInteraction = ({
  byUser = [],
  initialDate,
  finalDate
}) => {
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const textColor = getChartTextColor(isDark);
  const gridColor = getChartGridColor(isDark);

  const [selectedUser, setSelectedUser] = useState(null);

  const dataCharts = useMemo(
    () => ({
      labels: byUser.map(row => row.userName),
      datasets: [
        {
          label: i18n.t("dashboard.noInteraction.cardTitle"),
          data: byUser.map(row => Number(row.total) || 0),
          backgroundColor: BAR_COLOR
        }
      ]
    }),
    [byUser]
  );

  return (
    <>
      <Typography component="h2" variant="h6" color="primary" gutterBottom>
        {i18n.t("dashboard.noInteraction.cardTitle")}
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {i18n.t("dashboard.noInteraction.subtitle")}
      </Typography>

      {byUser.length === 0 ? (
        <Typography variant="body2">
          {i18n.t("dashboard.noInteraction.empty")}
        </Typography>
      ) : (
        <>
          <Bar
            options={buildOptions(textColor, gridColor)}
            data={dataCharts}
            style={{ maxWidth: "100%", maxHeight: "280px" }}
          />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("dashboard.noInteraction.user")}</TableCell>
                <TableCell align="right">
                  {i18n.t("dashboard.noInteraction.quantity")}
                </TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {byUser.map(row => (
                <TableRow key={row.userId ?? "sem-atendente"} hover>
                  <TableCell>{row.userName}</TableCell>
                  <TableCell align="right">{row.total}</TableCell>
                  <TableCell align="right">
                    {/* Botão explícito: deixa claro que dá para ver os chamados do atendente */}
                    <Button
                      size="small"
                      color="primary"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setSelectedUser(row)}
                    >
                      {i18n.t("dashboard.noInteraction.view")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}

      <NoInteractionTicketsModal
        open={!!selectedUser}
        onClose={() => setSelectedUser(null)}
        user={selectedUser}
        initialDate={initialDate}
        finalDate={finalDate}
      />
    </>
  );
};

export default ChartsClosedWithoutInteraction;
